import React, { useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageTemplate from "../../components/PageTemplate";
import Spinner from "../../components/Spinner";
import ErrorPage from "../../components/ErrorPage";
import { AuthContext } from "../../AuthProvider";
import { mapHttpErrorToMessage } from "../../utils/mapHttpErrorToMessage";
import {
  createParentMeetingPlan,
  getClassroom,
  getClassroomMeetingInviteOptions,
  type ClassroomMeetingInviteOption,
  type ClassroomSummary,
  type ParentMeetingAvailabilityBlockInput,
  type ParentMeetingRecurringAvailabilityInput,
} from "../../services/parentMeetingService";

import BackButton from "../../components/navigation/BackButton";
const PageTemplateComponent = PageTemplate as React.ComponentType<{
  title?: React.ReactNode;
  wrapperClassName?: string;
  children?: React.ReactNode;
}>;

const ErrorPageComponent = ErrorPage as React.ComponentType<{
  message: any;
  status?: any;
  onRetry?: () => void;
}>;

type TargetMode = "CLASSROOM" | "SPECIFIC";
type MeetingType = "PARENT_MEETING" | "PARENT_EVENING" | "INTAKE" | "CARE_CONVERSATION" | "OTHER";
type DayOfWeek = "MONDAY" | "TUESDAY" | "WEDNESDAY" | "THURSDAY" | "FRIDAY" | "SATURDAY" | "SUNDAY";

interface AvailabilityRow {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
}

interface RecurringAvailability {
  enabled: boolean;
  startDate: string;
  endDate: string;
  dayOfWeek: DayOfWeek;
  startTime: string;
  endTime: string;
}

const meetingTypeDefaults: Record<MeetingType, { title: string; durationMinutes: number }> = {
  PARENT_MEETING: { title: "Parent meeting", durationMinutes: 60 },
  PARENT_EVENING: { title: "Parent evening", durationMinutes: 10 },
  INTAKE: { title: "Intake meeting", durationMinutes: 30 },
  CARE_CONVERSATION: { title: "Care conversation", durationMinutes: 45 },
  OTHER: { title: "Meeting", durationMinutes: 30 },
};

const weekdays: DayOfWeek[] = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];
const weekdayIndex: Record<DayOfWeek, number> = {
  SUNDAY: 0,
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
};

function newAvailabilityRow(index: number): AvailabilityRow {
  return {
    id: `availability-${Date.now()}-${index}-${Math.random().toString(36).slice(2)}`,
    date: "",
    startTime: "",
    endTime: "",
  };
}

function makeAvailabilityRows(): AvailabilityRow[] {
  return [newAvailabilityRow(1)];
}

function makeRecurringDefault(): RecurringAvailability {
  return {
    enabled: false,
    startDate: "",
    endDate: "",
    dayOfWeek: "FRIDAY",
    startTime: "13:00",
    endTime: "17:00",
  };
}

function formatClassroomName(classroom: ClassroomSummary | null): string {
  return classroom?.className || classroom?.id || "";
}

function parseLocalDate(date: string): Date | null {
  if (!date) return null;
  const [year, month, day] = date.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function minutesFromTime(value: string): number | null {
  if (!value) return null;
  const [hours, minutes] = value.split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return hours * 60 + minutes;
}

function formatDate(value: string): string {
  const date = parseLocalDate(value);
  return date ? date.toLocaleDateString("nl-NL") : "";
}

function formatTime(value: string): string {
  if (!value) return "";
  const [hours, minutes] = value.split(":").map(Number);
  const date = new Date(2000, 0, 1, hours || 0, minutes || 0);
  return date.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
}

function formatRange(row: AvailabilityRow): string {
  if (!row.date || !row.startTime || !row.endTime) return "";
  return `${formatDate(row.date)} ${formatTime(row.startTime)} - ${formatTime(row.endTime)}`;
}

function countSlotsForTimeRange(startTime: string, endTime: string, durationMinutes: number): number {
  const start = minutesFromTime(startTime);
  const end = minutesFromTime(endTime);
  if (start == null || end == null || end <= start || durationMinutes < 1) return 0;
  return Math.floor((end - start) / durationMinutes);
}

function countRecurringDays(input: RecurringAvailability): number {
  const start = parseLocalDate(input.startDate);
  const end = parseLocalDate(input.endDate);
  if (!input.enabled || !start || !end || end < start) return 0;
  let count = 0;
  const cursor = new Date(start);
  while (cursor <= end) {
    if (cursor.getDay() === weekdayIndex[input.dayOfWeek]) count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
}

function hasValidBlock(row: AvailabilityRow): boolean {
  return Boolean(row.date) && countSlotsForTimeRange(row.startTime, row.endTime, 1) > 0;
}

function parentNames(candidate: ClassroomMeetingInviteOption): string {
  return [candidate.parent1Name, candidate.parent2Name].map((value) => value?.trim()).filter(Boolean).join(" & ");
}

function firstParentEmail(candidate: ClassroomMeetingInviteOption): string {
  return [candidate.parent1Email, candidate.parent2Email].filter(Boolean).join(" · ");
}

function inviteOptionKey(candidate: ClassroomMeetingInviteOption): string {
  return `${candidate.childId}:${candidate.familyId || "no-family"}`;
}

const ParentMeetingPlanner: React.FC = () => {
  const { keycloak } = useContext(AuthContext);
  const { classroomId } = useParams<{ classroomId: string }>();
  const { t } = useTranslation();

  const [classroom, setClassroom] = useState<ClassroomSummary | null>(null);
  const [inviteOptions, setInviteOptions] = useState<ClassroomMeetingInviteOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<{ status?: number; message?: string } | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [meetingType, setMeetingType] = useState<MeetingType>("PARENT_MEETING");
  const [title, setTitle] = useState(meetingTypeDefaults.PARENT_MEETING.title);
  const [description, setDescription] = useState("");
  const [durationMinutes, setDurationMinutes] = useState(meetingTypeDefaults.PARENT_MEETING.durationMinutes);
  const [targetMode, setTargetMode] = useState<TargetMode>("CLASSROOM");
  const [availabilityRows, setAvailabilityRows] = useState<AvailabilityRow[]>(makeAvailabilityRows);
  const [recurringAvailability, setRecurringAvailability] = useState<RecurringAvailability>(makeRecurringDefault);
  const [selectedInviteOptionKeys, setSelectedInviteOptionKeys] = useState<string[]>([]);

  const selectedInviteOptions = useMemo(() => {
    if (targetMode === "CLASSROOM") return inviteOptions;
    const selected = new Set(selectedInviteOptionKeys);
    return inviteOptions.filter((candidate) => selected.has(inviteOptionKey(candidate)));
  }, [inviteOptions, selectedInviteOptionKeys, targetMode]);

  const validRows = useMemo(() => availabilityRows.filter(hasValidBlock), [availabilityRows]);
  const blockSlotCount = useMemo(
    () => validRows.reduce((total, row) => total + countSlotsForTimeRange(row.startTime, row.endTime, durationMinutes), 0),
    [durationMinutes, validRows]
  );
  const recurringDayCount = useMemo(() => countRecurringDays(recurringAvailability), [recurringAvailability]);
  const recurringSlotsPerDay = useMemo(
    () => countSlotsForTimeRange(recurringAvailability.startTime, recurringAvailability.endTime, durationMinutes),
    [durationMinutes, recurringAvailability.endTime, recurringAvailability.startTime]
  );
  const recurringSlotCount = recurringAvailability.enabled ? recurringDayCount * recurringSlotsPerDay : 0;
  const previewSlotCount = blockSlotCount + recurringSlotCount;

  useEffect(() => {
    if (!keycloak || !classroomId) return;
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        console.debug("[ParentMeetingPlanner] loading invite options", {
          classroomId,
          functionName: "getClassroomMeetingInviteOptions",
          endpoint: `/classrooms/${classroomId}/meeting-invite-options`,
        });
        const [loadedClassroom, loadedInviteOptions] = await Promise.all([
          getClassroom(classroomId),
          getClassroomMeetingInviteOptions(classroomId),
        ]);
        if (cancelled) return;
        setClassroom(loadedClassroom);
        setInviteOptions(loadedInviteOptions);
        console.debug("[ParentMeetingPlanner] invite options loaded", {
          classroomId,
          count: loadedInviteOptions.length,
        });
      } catch (err: any) {
        const mapped = mapHttpErrorToMessage(err, t);
        if (!cancelled) {
          setError({
            status: err?.response?.status,
            message:
              typeof mapped === "string"
                ? mapped
                : mapped?.message ||
                  mapped?.title ||
                  t("parentMeetings.planner.loadFailed", {
                    defaultValue: "Could not load classroom information.",
                  }),
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [classroomId, keycloak, t]);

  useEffect(() => {
    if (targetMode === "CLASSROOM") return;
    setSelectedInviteOptionKeys((current) => current.filter((key) => inviteOptions.some((candidate) => inviteOptionKey(candidate) === key)));
  }, [inviteOptions, targetMode]);

  const updateAvailabilityRow = (rowId: string, patch: Partial<AvailabilityRow>) => {
    setAvailabilityRows((current) => current.map((row) => (row.id === rowId ? { ...row, ...patch } : row)));
  };

  const updateRecurringAvailability = (patch: Partial<RecurringAvailability>) => {
    setRecurringAvailability((current) => ({ ...current, ...patch }));
  };

  const addAvailabilityRow = () => {
    setAvailabilityRows((current) => [...current, newAvailabilityRow(current.length + 1)]);
  };

  const removeAvailabilityRow = (rowId: string) => {
    setAvailabilityRows((current) => (current.length <= 1 ? current : current.filter((row) => row.id !== rowId)));
  };

  const changeMeetingType = (value: MeetingType) => {
    setMeetingType(value);
    const defaults = meetingTypeDefaults[value];
    setTitle(defaults.title);
    setDurationMinutes(defaults.durationMinutes);
  };

  const toggleCandidate = (candidate: ClassroomMeetingInviteOption) => {
    const key = inviteOptionKey(candidate);
    setSelectedInviteOptionKeys((current) =>
      current.includes(key) ? current.filter((value) => value !== key) : [...current, key]
    );
  };

  const hasRecurringInput = recurringAvailability.enabled && recurringDayCount > 0 && recurringSlotsPerDay > 0;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!classroomId) return;
    setSaving(true);
    setError(null);
    setSuccess(null);

    if (previewSlotCount === 0) {
      setSaving(false);
      setError({
        message: t("parentMeetings.planner.validation.noAvailability", {
          defaultValue: "Add at least one valid availability block or recurring range.",
        }),
      });
      return;
    }

    if (selectedInviteOptions.length === 0) {
      setSaving(false);
      setError({
        message: t("parentMeetings.planner.validation.noFamilies", {
          defaultValue: "Select at least one child to invite.",
        }),
      });
      return;
    }

    const availabilityBlocks: ParentMeetingAvailabilityBlockInput[] = validRows.map((row) => ({
      date: row.date,
      startTime: row.startTime,
      endTime: row.endTime,
    }));
    const recurring: ParentMeetingRecurringAvailabilityInput = {
      enabled: hasRecurringInput,
      startDate: recurringAvailability.startDate,
      endDate: recurringAvailability.endDate,
      dayOfWeek: recurringAvailability.dayOfWeek,
      startTime: recurringAvailability.startTime,
      endTime: recurringAvailability.endTime,
    };

    try {
      await createParentMeetingPlan({
        classroomId,
        meetingType,
        title,
        description: description || undefined,
        durationMinutes,
        targetMode,
        familyIds: [],
        selectedChildren:
          targetMode === "CLASSROOM"
            ? []
            : selectedInviteOptions.map((option) => ({
                childId: option.childId,
                familyId: option.familyId,
              })),
        availabilityBlocks,
        recurringAvailability: recurring,
      });
      setSuccess(
        t("parentMeetings.planner.created", {
          defaultValue: "The parent meeting planning has been created and invitations have been sent.",
        })
      );
    } catch (err: any) {
      const mapped = mapHttpErrorToMessage(err, t);
      setError({
        status: err?.response?.status,
        message:
          typeof mapped === "string"
            ? mapped
            : mapped?.message ||
              mapped?.title ||
              t("parentMeetings.planner.saveFailed", {
                defaultValue: "Could not create the parent meeting planning.",
              }),
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spinner />;
  if (error?.status && !error.message) {
    return <ErrorPageComponent message={error.message} status={error.status} onRetry={() => window.location.reload()} />;
  }

  return (
    <PageTemplateComponent
      title={t("parentMeetings.title", { defaultValue: "Parent meetings" })}
      wrapperClassName="max-w-5xl mx-auto"
    >
      <form onSubmit={handleSubmit} className="mx-auto flex max-w-5xl flex-col gap-4 text-gray-900 dark:text-gray-100">
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {t("parentMeetings.planner.classroomLabel", { defaultValue: "Classroom" })}
              </p>
              <h1 className="mt-0.5 text-xl font-semibold text-gray-950 dark:text-white">{formatClassroomName(classroom)}</h1>
            </div>
            <BackButton fallback={`/school/classrooms/${classroomId}/details`} />
          </div>
          {error?.message && (
            <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-800 dark:border-red-500/40 dark:bg-red-950/40 dark:text-red-100">
              {error.message}
            </div>
          )}
          {success && (
            <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm font-medium text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-950/40 dark:text-emerald-100">
              {success}
            </div>
          )}
        </div>

        <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <h2 className="text-base font-semibold text-gray-950 dark:text-white">1. {t("parentMeetings.planner.sections.meeting", { defaultValue: "Meeting" })}</h2>
          <div className="mt-3 grid gap-3 md:grid-cols-[0.8fr_1.1fr_0.55fr]">
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-gray-700 dark:text-gray-200">{t("parentMeetings.planner.meetingType", { defaultValue: "Meeting type" })}</span>
              <select value={meetingType} onChange={(event) => changeMeetingType(event.target.value as MeetingType)} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-950">
                <option value="PARENT_MEETING">{t("parentMeetings.types.PARENT_MEETING", { defaultValue: "Parent meeting" })}</option>
                <option value="PARENT_EVENING">{t("parentMeetings.types.PARENT_EVENING", { defaultValue: "Parent evening" })}</option>
                <option value="INTAKE">{t("parentMeetings.types.INTAKE", { defaultValue: "Intake meeting" })}</option>
                <option value="CARE_CONVERSATION">{t("parentMeetings.types.CARE_CONVERSATION", { defaultValue: "Care conversation" })}</option>
                <option value="OTHER">{t("parentMeetings.types.OTHER", { defaultValue: "Other" })}</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-gray-700 dark:text-gray-200">{t("parentMeetings.planner.title", { defaultValue: "Title" })}</span>
              <input value={title} onChange={(event) => setTitle(event.target.value)} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-950" required />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-gray-700 dark:text-gray-200">{t("parentMeetings.planner.duration", { defaultValue: "Duration" })}</span>
              <select value={durationMinutes} onChange={(event) => setDurationMinutes(Number(event.target.value))} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-950">
                {[10, 15, 20, 30, 45, 60].map((minutes) => (
                  <option key={minutes} value={minutes}>{minutes} {t("common.minutes", { defaultValue: "minutes" })}</option>
                ))}
              </select>
            </label>
          </div>
          <label className="mt-3 flex flex-col gap-1 text-sm">
            <span className="font-medium text-gray-700 dark:text-gray-200">{t("parentMeetings.planner.description", { defaultValue: "Description" })}</span>
            <textarea value={description} onChange={(event) => setDescription(event.target.value)} rows={2} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-950" placeholder={t("parentMeetings.planner.descriptionPlaceholder", { defaultValue: "Optional message for parents." })} />
          </label>
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-base font-semibold text-gray-950 dark:text-white">2. {t("parentMeetings.planner.sections.availability", { defaultValue: "Availability" })}</h2>
            <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-semibold text-blue-800 dark:border-blue-500/40 dark:bg-blue-950/40 dark:text-blue-100">
              {previewSlotCount} {t("parentMeetings.planner.availableSlots", { defaultValue: "available slots" })}
            </div>
          </div>

          <div className="mt-3 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
            <div className="mb-2 flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t("parentMeetings.planner.singleBlocks", { defaultValue: "Single time blocks" })}</h3>
              <span className="text-xs text-gray-500 dark:text-gray-400">{blockSlotCount} {t("parentMeetings.planner.slotsSelected", { defaultValue: "slots" })}</span>
            </div>
            <div className="grid gap-2">
              {availabilityRows.map((row, index) => {
                const valid = hasValidBlock(row);
                return (
                  <div key={row.id} className="grid items-end gap-2 rounded-lg bg-gray-50 p-2 md:grid-cols-[28px_1fr_1fr_1fr_32px] dark:bg-gray-950/40">
                    <div className="flex h-8 w-8 items-center justify-center rounded-md bg-white text-xs font-bold text-gray-700 dark:bg-gray-900 dark:text-gray-200">{index + 1}</div>
                    <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                      {t("parentMeetings.planner.date", { defaultValue: "Date" })}
                      <input type="date" value={row.date} onChange={(event) => updateAvailabilityRow(row.id, { date: event.target.value })} className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm font-normal dark:border-gray-700 dark:bg-gray-950" />
                      {row.date && <span className="font-normal text-gray-500">{formatDate(row.date)}</span>}
                    </label>
                    <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                      {t("parentMeetings.planner.from", { defaultValue: "From" })}
                      <input type="time" value={row.startTime} onChange={(event) => updateAvailabilityRow(row.id, { startTime: event.target.value })} className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm font-normal dark:border-gray-700 dark:bg-gray-950" />
                    </label>
                    <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                      {t("parentMeetings.planner.until", { defaultValue: "Until" })}
                      <input type="time" value={row.endTime} onChange={(event) => updateAvailabilityRow(row.id, { endTime: event.target.value })} className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm font-normal dark:border-gray-700 dark:bg-gray-950" />
                      {row.date && row.startTime && row.endTime && !valid && <span className="text-xs font-medium text-red-600 dark:text-red-300">{t("parentMeetings.planner.invalidRange", { defaultValue: "End time must be after start time." })}</span>}
                    </label>
                    <button type="button" onClick={() => removeAvailabilityRow(row.id)} disabled={availabilityRows.length <= 1} className="flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white text-lg text-gray-500 hover:border-red-300 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-30 dark:border-gray-700 dark:bg-gray-950" aria-label={t("parentMeetings.planner.removeBlock", { defaultValue: "Remove block" })}>×</button>
                  </div>
                );
              })}
            </div>
            <button type="button" onClick={addAvailabilityRow} className="mt-2 flex w-full items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-200">
              + {t("parentMeetings.planner.addBlock", { defaultValue: "Add time block" })}
            </button>
            {validRows.length > 0 && <div className="mt-2 flex flex-wrap gap-2 text-xs text-gray-600 dark:text-gray-300">{validRows.map((row) => <span key={row.id} className="rounded-md border border-gray-200 px-2 py-1 dark:border-gray-700">{formatRange(row)}</span>)}</div>}
          </div>

          <div className="mt-3 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
              <input type="checkbox" checked={recurringAvailability.enabled} onChange={(event) => updateRecurringAvailability({ enabled: event.target.checked })} className="h-4 w-4 rounded border-gray-300" />
              {t("parentMeetings.planner.recurringAvailability", { defaultValue: "Recurring availability" })}
            </label>
            <div className="mt-3 grid gap-2 md:grid-cols-[1fr_1fr_1fr_0.8fr_0.8fr]">
              <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                {t("parentMeetings.planner.startDate", { defaultValue: "Start date" })}
                <input type="date" value={recurringAvailability.startDate} onChange={(event) => updateRecurringAvailability({ startDate: event.target.value })} disabled={!recurringAvailability.enabled} className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm font-normal disabled:opacity-60 dark:border-gray-700 dark:bg-gray-950" />
                {recurringAvailability.startDate && <span className="font-normal text-gray-500">{formatDate(recurringAvailability.startDate)}</span>}
              </label>
              <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                {t("parentMeetings.planner.endDate", { defaultValue: "End date" })}
                <input type="date" value={recurringAvailability.endDate} onChange={(event) => updateRecurringAvailability({ endDate: event.target.value })} disabled={!recurringAvailability.enabled} className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm font-normal disabled:opacity-60 dark:border-gray-700 dark:bg-gray-950" />
                {recurringAvailability.endDate && <span className="font-normal text-gray-500">{formatDate(recurringAvailability.endDate)}</span>}
              </label>
              <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                {t("parentMeetings.planner.weekday", { defaultValue: "Weekday" })}
                <select value={recurringAvailability.dayOfWeek} onChange={(event) => updateRecurringAvailability({ dayOfWeek: event.target.value as DayOfWeek })} disabled={!recurringAvailability.enabled} className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm font-normal disabled:opacity-60 dark:border-gray-700 dark:bg-gray-950">
                  {weekdays.map((day) => <option key={day} value={day}>{t(`parentMeetings.weekdays.${day}`, { defaultValue: day })}</option>)}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                {t("parentMeetings.planner.from", { defaultValue: "From" })}
                <input type="time" value={recurringAvailability.startTime} onChange={(event) => updateRecurringAvailability({ startTime: event.target.value })} disabled={!recurringAvailability.enabled} className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm font-normal disabled:opacity-60 dark:border-gray-700 dark:bg-gray-950" />
              </label>
              <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                {t("parentMeetings.planner.until", { defaultValue: "Until" })}
                <input type="time" value={recurringAvailability.endTime} onChange={(event) => updateRecurringAvailability({ endTime: event.target.value })} disabled={!recurringAvailability.enabled} className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm font-normal disabled:opacity-60 dark:border-gray-700 dark:bg-gray-950" />
              </label>
            </div>
            <div className="mt-2 rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-700 dark:bg-gray-950/40 dark:text-gray-200">
              {recurringAvailability.enabled
                ? t("parentMeetings.planner.recurringPreview", {
                    defaultValue: "Every {{weekday}} from {{from}} to {{until}} generates {{count}} slots.",
                    weekday: t(`parentMeetings.weekdays.${recurringAvailability.dayOfWeek}`, { defaultValue: recurringAvailability.dayOfWeek }),
                    from: formatTime(recurringAvailability.startTime),
                    until: formatTime(recurringAvailability.endTime),
                    count: recurringSlotCount,
                  })
                : t("parentMeetings.planner.recurringDisabled", { defaultValue: "Enable recurring availability to generate slots across a date range." })}
            </div>
          </div>
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-base font-semibold text-gray-950 dark:text-white">3. {t("parentMeetings.planner.sections.target", { defaultValue: "Target group" })}</h2>
            <div className="flex rounded-lg border border-gray-200 bg-gray-50 p-1 text-sm font-semibold dark:border-gray-700 dark:bg-gray-950/40">
              <button type="button" onClick={() => setTargetMode("CLASSROOM")} className={`rounded-md px-3 py-1.5 ${targetMode === "CLASSROOM" ? "bg-white text-blue-700 shadow dark:bg-gray-900 dark:text-blue-200" : "text-gray-600 dark:text-gray-300"}`}>{t("parentMeetings.planner.wholeClassroom", { defaultValue: "Hele klas" })}</button>
              <button type="button" onClick={() => setTargetMode("SPECIFIC")} className={`rounded-md px-3 py-1.5 ${targetMode === "SPECIFIC" ? "bg-white text-blue-700 shadow dark:bg-gray-900 dark:text-blue-200" : "text-gray-600 dark:text-gray-300"}`}>{t("parentMeetings.planner.specificChildren", { defaultValue: "Specifieke kinderen" })}</button>
            </div>
          </div>
          {inviteOptions.length === 0 ? (
            <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-950/40 dark:text-gray-300">{t("parentMeetings.planner.noChildren", { defaultValue: "No children found in this classroom." })}</div>
          ) : (
            <div className="mt-3 grid gap-2 md:grid-cols-2">
              {inviteOptions.map((candidate) => {
                const checked = targetMode === "CLASSROOM" || selectedInviteOptionKeys.includes(inviteOptionKey(candidate));
                const disabled = targetMode === "CLASSROOM";
                return (
                  <button key={inviteOptionKey(candidate)} type="button" disabled={disabled} onClick={() => toggleCandidate(candidate)} className={`rounded-lg border p-3 text-left transition ${checked ? "border-blue-300 bg-blue-50 dark:border-blue-500/40 dark:bg-blue-950/40" : "border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-950/30"} ${disabled ? "cursor-default" : "cursor-pointer"}`}>
                    <div className="flex items-start gap-2">
                      <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border text-xs ${checked ? "border-blue-600 bg-blue-600 text-white" : "border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-900"}`}>{checked ? "✓" : ""}</span>
                      <span className="min-w-0">
                        <span className="block font-semibold text-gray-950 dark:text-white">{candidate.childName}</span>
                        <span className="mt-0.5 block text-sm text-gray-600 dark:text-gray-300">{parentNames(candidate) || t("parentMeetings.planner.parentsUnknown", { defaultValue: "Parents unknown" })}</span>
                        <span className="mt-0.5 block truncate text-xs font-medium text-gray-500 dark:text-gray-400">{firstParentEmail(candidate) || t("parentMeetings.planner.emailMissing", { defaultValue: "No email address found" })}</span>
                        {["SEPARATED", "DIVORCED"].includes(candidate.familySituation || "") && (
                          <span className="mt-1 inline-flex rounded-md bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                            {t("parentMeetings.planner.separateHouseholdsFamily", { defaultValue: "Separate households" })}
                          </span>
                        )}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        <section className="sticky bottom-4 z-10 rounded-xl border border-gray-200 bg-white/95 p-4 shadow-lg backdrop-blur dark:border-gray-800 dark:bg-gray-950/90">
          <h2 className="sr-only">4. {t("parentMeetings.planner.sections.review", { defaultValue: "Review and publish" })}</h2>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-gray-600 dark:text-gray-300">
              <span className="font-semibold text-gray-950 dark:text-white">{selectedInviteOptions.length}</span> {t("parentMeetings.planner.childrenSelected", { defaultValue: "children" })} · <span className="font-semibold text-gray-950 dark:text-white">{previewSlotCount}</span> {t("parentMeetings.planner.slotsSelected", { defaultValue: "slots" })}
            </div>
            <button type="submit" disabled={saving || previewSlotCount === 0 || selectedInviteOptions.length === 0} className="inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60">
              {saving ? t("common.saving", { defaultValue: "Saving..." }) : t("parentMeetings.planner.publish", { defaultValue: "Create and send invitations" })}
            </button>
          </div>
        </section>
      </form>
    </PageTemplateComponent>
  );
};

export default ParentMeetingPlanner;
